import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  calculateStarFillPercentage,
  getOffsetPercentage,
  roundToStep,
  scaleStarValueToRating,
  snapToHalfStar,
} from '@components/StarRating/helpers/starRatingHelpers';

import { getIsRtlState } from '@redux/selectors';

import StarIcon from './StarIcon';
import styles from './StarRating.module.scss';

const TOTAL_STARS = 5;
const DEFAULT_EMPTY_INTERNAL_RATING_STAR_BORDER_COLOR = '#767676';
const DEFAULT_FILLED_STAR_BORDER_COLOR = '#FF9800';
const DEFAULT_EMPTY_STAR_BORDER_COLOR = '#9E9E9E';

/**
 * Props for the StarRating component
 */
export type StarRatingProps = {
  /** The current rating value */
  value: number;
  /** Callback function called when the rating changes */
  onChange?: (value: number) => void;
  /** Maximum rating value (defaults to 5) */
  maxValue?: number;
  /** Optional labels to display below the stars */
  labels?: { min: string; max: string };
  /** Whether to allow half-star ratings */
  enableHalfStar?: boolean;
  /** Size of the stars */
  size?: 'small' | 'big';
  starStyle?: {
    emptyInternalRatingStarBorderColor?: string;
    filledStarBorderColor?: string;
    emptyStarBorderColor?: string;
  };
  className?: string;
};

const StarRating: React.FC<StarRatingProps> = ({
  value,
  onChange,
  maxValue = TOTAL_STARS,
  labels,
  enableHalfStar = true,
  size = 'big',
  starStyle,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [isTouchActive, setIsTouchActive] = useState(false);
  const [internalRating, setInternalRating] = useState(value);
  const isRtl: boolean = useSelector(getIsRtlState);

  // Update internal rating when prop value changes
  useEffect(() => {
    setInternalRating(value);
  }, [value]);

  const calculateValueFromEvent = useCallback(
    (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>): number | null => {
      if (!containerRef.current) return null;

      const percentage = getOffsetPercentage(event, containerRef.current, isRtl);
      const rawStarValue = percentage * TOTAL_STARS;
      const snappedStarValue = snapToHalfStar(rawStarValue, enableHalfStar);
      const calculatedValue = scaleStarValueToRating(snappedStarValue, maxValue, TOTAL_STARS);

      // Ensure value is within bounds [0, maxValue]
      const finalValue = Math.min(maxValue, Math.max(0, calculatedValue));

      // Round to the nearest half-star step for precision
      return enableHalfStar
        ? roundToStep(finalValue, maxValue, TOTAL_STARS)
        : Math.round(finalValue);
    },
    [maxValue, isRtl, enableHalfStar],
  );

  const handleInteractionMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
      // Only handle mouse events here, touch events are handled separately
      if (!('clientX' in event) || !isMouseDown || !onChange) return;

      // Prevent default behavior to stop scrolling
      event.preventDefault();

      // Process the mouse drag
      const calculatedValue = calculateValueFromEvent(event);
      if (calculatedValue !== null) {
        setInternalRating(calculatedValue);
        onChange(calculatedValue);
      }
    },
    [isMouseDown, onChange, calculateValueFromEvent],
  );

  const handleMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!onChange) return;

      setIsMouseDown(true);

      // Apply value immediately on click down
      const calculatedValue = calculateValueFromEvent(event);
      if (calculatedValue !== null) {
        setInternalRating(calculatedValue);
        onChange(calculatedValue);
      }

      // Prevent text selection during drag
      event.preventDefault();
    },
    [onChange, calculateValueFromEvent],
  );

  const handleMouseUp = useCallback(() => {
    setIsMouseDown(false);
  }, []);

  // Effect to add/remove global mouseup listener
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      setIsMouseDown(false);
    };

    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('mouseleave', handleGlobalMouseUp);

    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('mouseleave', handleGlobalMouseUp);
    };
  }, []);

  const handleTouchStart = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      if (!onChange) return;
      setIsTouchActive(true);

      event.preventDefault();

      const calculatedValue = calculateValueFromEvent(event);

      if (calculatedValue !== null) {
        setInternalRating(calculatedValue);
        onChange(calculatedValue);
      }
    },
    [onChange, calculateValueFromEvent],
  );

  const handleTouchMove = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      if (!isTouchActive || !onChange) return;

      event.preventDefault();

      const calculatedValue = calculateValueFromEvent(event);
      if (calculatedValue !== null) {
        setInternalRating(calculatedValue);
        onChange(calculatedValue);
      }
    },
    [isTouchActive, onChange, calculateValueFromEvent],
  );

  const handleTouchEnd = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    setIsTouchActive(false);

    event.preventDefault();
  }, []);

  // Use capture phase for touch events to ensure they're handled before scrolling
  useEffect(() => {
    const currentRef = containerRef.current;
    if (!currentRef) return;

    const touchMoveHandler = (e: TouchEvent) => {
      if (isTouchActive) {
        // Prevent scrolling during active star rating touch
        e.preventDefault();
      }
    };

    currentRef.addEventListener('touchmove', touchMoveHandler, { passive: false });

    return () => {
      currentRef.removeEventListener('touchmove', touchMoveHandler);
    };
  }, [isTouchActive]);

  // Create array of indices and reverse if RTL
  const starIndices = [...Array(TOTAL_STARS)].map((_, i) => i);
  if (isRtl) {
    starIndices.reverse();
  }

  return (
    <div className={clsx(styles.starRatingContainer, className)}>
      <div
        ref={containerRef}
        className={clsx(styles.stars, internalRating > 0 && styles.hasValue, styles[size])}
        // Mouse Events
        onMouseDown={handleMouseDown}
        onMouseMove={handleInteractionMove}
        onMouseUp={handleMouseUp}
        // Touch Events
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        // Cancel touch
        onTouchCancel={() => setIsTouchActive(false)}
        // Accessibility
        role='slider'
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={maxValue}
        aria-label='Star Rating'
        tabIndex={0}
        data-testid='star-rating-container'
      >
        {starIndices.map((index) => {
          const fillPercentage = calculateStarFillPercentage(
            index,
            internalRating,
            maxValue,
            isRtl,
            TOTAL_STARS,
          );

          const borderColor =
            internalRating === 0
              ? starStyle?.emptyInternalRatingStarBorderColor ||
                DEFAULT_EMPTY_INTERNAL_RATING_STAR_BORDER_COLOR
              : fillPercentage > 0
              ? starStyle?.filledStarBorderColor || DEFAULT_FILLED_STAR_BORDER_COLOR
              : starStyle?.emptyStarBorderColor || DEFAULT_EMPTY_STAR_BORDER_COLOR;

          const starContainerStyle: React.CSSProperties = {
            '--star-border-color': borderColor,
          } as React.CSSProperties;

          // Apply mask styles directly for better control
          const foregroundStyle: React.CSSProperties = {
            WebkitMaskImage: isRtl
              ? `linear-gradient(to left, black ${fillPercentage}%, transparent ${fillPercentage}%)`
              : `linear-gradient(to right, black ${fillPercentage}%, transparent ${fillPercentage}%)`,
            maskImage: isRtl
              ? `linear-gradient(to left, black ${fillPercentage}%, transparent ${fillPercentage}%)`
              : `linear-gradient(to right, black ${fillPercentage}%, transparent ${fillPercentage}%)`,
          };

          return (
            <div
              key={index}
              className={styles.star}
              style={starContainerStyle}
              data-testid={`star-${index + 1}`}
            >
              <StarIcon className={styles.starBackground} aria-hidden='true' />
              <div className={styles.starForegroundWrapper}>
                <StarIcon
                  className={styles.starForeground}
                  aria-hidden='true'
                  style={foregroundStyle}
                />
              </div>

              {labels && index === 0 && (
                <span className={clsx(styles.labels, styles.minLabel)}>
                  {isRtl ? labels.max : labels.min}
                </span>
              )}
              {labels && index === TOTAL_STARS - 1 && (
                <span className={clsx(styles.labels, styles.maxLabel)}>
                  {isRtl ? labels.min : labels.max}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StarRating;
